import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { debounce } from "lodash";
import { ObjectSchema } from "yup";
import useFormWatch from "@rf-hooks/useFormWatch";

/**
 * @returns Object containing validation errors and validation state
 * @description This hook is used to validate the fields of the property form when form values change
 */

export interface IUseValidatorFieldsPropertyProps {
  fieldNames?: string[];
  schema: ObjectSchema<any>;
  parserNameFn?: (fieldName: string) => string;
}

const useFormFieldValidationHit = (props: IUseValidatorFieldsPropertyProps) => {
  const { getValues } = useFormContext();
  const state = useFormWatch(props.fieldNames as string[]);
  const [isValidationRunning, setIsValidationRunning] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const debValidate = debounce(() => {
    validateFields();
  }, 500);

  const validateFields = async () => {
    if (isValidationRunning) {
      return;
    }
    setIsValidationRunning(true);
    try {
      await props.schema.validate(getValues(), {
        abortEarly: false,
        strict: false,
        context: state // Pass the form state as context for conditional validation
      });
      setErrors({});
    } catch (err: any) {
      const newErrors: Record<string, string> = {};
      if (err.inner && Array.isArray(err.inner)) {
        filterErrors(err.inner, props?.fieldNames || []).forEach((error: any) => {
          // Use label if available, otherwise path
          if (error.path.includes("[")) {
            if (props?.parserNameFn) {
              const parsedName = props.parserNameFn(error.path);
              newErrors[parsedName] = error.message;
            } else {
              newErrors[error.path] = error.message;
            }
          } else if (error.params?.label) {
            newErrors[error.params.label] = error.message;
          } else {
            newErrors[error.path] = error.message;
          }
        });
      } else {
        // Handle unexpected error formats
        console.log("Error:", { ...err });
        newErrors["general"] = "Error de validación general";
      }
      setErrors(newErrors);
    } finally {
      setIsValidationRunning(false);
    }
  };

  useEffect(() => {
    debValidate();
  }, [JSON.stringify(state)]);

  return {
    errors,
    isValidationRunning
  } as {
    errors: Record<string, string>;
    isValidationRunning: boolean;
  };
};

export default useFormFieldValidationHit;

const filterErrors = (errors: any[], fieldNames: string[]) => {
  return errors.filter(x => {
    if (fieldNames.includes(x.path)) {
      return x;
    } else if (x.path.includes("[")) {
      const path = x.path.split("[")[0];
      if (fieldNames.includes(path)) {
        return x;
      }
    } else {
      return;
    }
  });
};
